import type { ApiTemplateCustomization } from '@Model';
import { getControlTypeByKeyword, type ControlType } from './controlTypes';
import { splitName, type NameParts } from './keywords';

/**
 * An `ApiTemplateCustomization` object augmented with its `group` and `keyword` parts.
 */
export type ProcessedCustomization = ApiTemplateCustomization & NameParts;

/**
 * Represents a group of related control customizations, such as X and Y offsets for an element.
 */
export type ControlGroup = {
    control: ControlType;
    customizations: ProcessedCustomization[];
};

/**
 * Represents a final item within a group, which can be either the main customization
 * or a single `ControlGroup` containing all its related controls.
 */
export type GroupedCustomization = ProcessedCustomization | ControlGroup;

/**
 * The final, structured result. It maps a dataset name to a collection of groups,
 * where each group contains its main item and an optional control group.
 * @example { customization: { header: [...] }, logo: { logo: [...] } }
 */
export type GroupedCustomizations = Record<
    string, // dataset
    Record<string, GroupedCustomization[]> // group
>;

/**
 * Groups a flat list of customizations into a structured, nested object.
 * This version assumes a main item will always exist for any given group
 * and builds the group structure on the fly.
 *
 * @param customizations The flat array of API template customizations.
 * @returns A structured object with items grouped by dataset and name.
 */
export const groupCustomizations = (customizations: ApiTemplateCustomization[]): GroupedCustomizations => {
    // Use an intermediate map to build the groups directly.
    const grouped = new Map<string, GroupedCustomization[]>();
    // Use a second map to store the final dataset key for each group.
    const groupMetadata = new Map<string, { datasetKey: string }>();

    for (const item of customizations) {
        const { group, keyword } = splitName(item.name);
        const processedItem: ProcessedCustomization = { ...item, group, keyword };
        const controlType = keyword ? getControlTypeByKeyword(keyword) : null;

        if (!grouped.has(group)) {
            grouped.set(group, []);
        }

        const groupItems = grouped.get(group)!;

        if (controlType === null) {
            groupItems.unshift(processedItem);
            groupMetadata.set(group, { datasetKey: processedItem.dataset });
        } else {
            let controlGroup = groupItems.find((g): g is ControlGroup => 'control' in g);

            if (!controlGroup) {
                controlGroup = { control: controlType, customizations: [] };
                groupItems.push(controlGroup);
            }

            controlGroup.customizations.push(processedItem);
        }
    }

    // Final step: Assemble the result object from the prepared groups.
    const result: GroupedCustomizations = {};
    for (const [groupName, items] of grouped.entries()) {
        const metadata = groupMetadata.get(groupName)!;

        const { datasetKey } = metadata;
        result[datasetKey] ??= {};
        result[datasetKey][groupName] = items;
    }

    return result;
};
